import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { DISCORD_URL } from "@/lib/config";
import useDiscordWidget from "@/hooks/useDiscordWidget";
import useGtpsStats from "@/hooks/useGtpsStats";

const marqueeItems = [
  "Custom worlds",
  "Daily drops",
  "Fair economy",
  "Anti-cheat",
  "Active staff",
  "Weekly events",
  "Mythic items",
  "Premium polish",
  "Low latency",
  "24/7 online",
];

export default function Stats() {
  const { data, loading } = useDiscordWidget();
  const gtps = useGtpsStats();
  const presence = data?.online ?? 0;
  const discordMembers = loading ? "—" : (data?.total_members ?? presence).toLocaleString();
  const onlinePlayers = gtps.loading ? "—" : (gtps.data?.online_players ?? 0).toLocaleString();

  const stats = [
    { value: discordMembers, label: "Discord members", testid: "stat-discord-members" },
    { value: onlinePlayers, label: "Online players", testid: "stat-online-players" },
    { value: "12ms", label: "Average latency", testid: "stat-avg-latency" },
    { value: "99.9%", label: "Uptime", testid: "stat-uptime" },
  ];

  return (
    <section
      id="stats"
      data-testid="stats-section"
      className="relative mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-28"
    >
      {/* Marquee */}
      <div className="relative mb-16 overflow-hidden">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#030305] to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#030305] to-transparent z-10" />
        <div className="flex w-max animate-marquee gap-3">
          {[...marqueeItems, ...marqueeItems].map((m, i) => (
            <span
              key={`${m}-${i}`}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs uppercase tracking-[0.18em] text-slate-300"
            >
              <span className="h-1 w-1 rounded-full bg-amber-300" />
              {m}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-stretch">
        {/* CTA card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative glass-card ring-border p-8 lg:p-10 lg:col-span-3 overflow-hidden text-center sm:text-left"
          data-testid="cta-card"
        >
          <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-amber-500/15 blur-3xl" />
          <div className="absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-slate-300">
              Ready to play?
            </div>
            <h3 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
              Your world is waiting.
            </h3>
            <p className="mt-3 max-w-md mx-auto sm:mx-0 text-sm sm:text-base text-slate-400">
              Join our Discord to get the latest updates, drop schedules and
              direct support from the OriginPS team.
            </p>
            <div className="mt-7 flex justify-center sm:justify-start">
              <a
                href={DISCORD_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="cta-join-discord-btn"
                className="glossy-gold inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-transform hover:scale-[1.03] active:scale-[0.98]"
              >
                Join Discord
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </motion.div>

        {/* Stats grid */}
        <div className="lg:col-span-2 grid grid-cols-2 gap-4">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{
                duration: 0.6,
                delay: 0.05 * i,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="glass-card ring-border p-5 sm:p-6"
              data-testid={s.testid}
            >
              <div className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-gradient-white">
                {s.value}
              </div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.18em] text-slate-400">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
